"use client"
import { IconButton, Text } from "@/elements"
import { TextLink } from "@/elements/TextLink"
import { graphql } from "@/gql"
import { GetSimpleProjectDocument, GetSimpleProjectQuery } from "@/gql/graphql"
import { useLazyQuery, useQuery } from "@apollo/client"
import { ArrowLeftIcon, XMarkIcon } from "@heroicons/react/24/outline"
import clsx from "clsx"
import { ProjectMedia } from "../../../app/(portfolio)/[handle]/projects/[projectId]/components/ProjectMedia"
import { DrawerContent } from "../Drawer"
import { RolePreview } from "../JobApplication/RolePreview"
import { useEffect, useState } from "react"

graphql(`
  query GetSimpleProject($projectId: ID!) {
    creativeWorkPortfolioProject(id: $projectId) {
      id
      title
      description
      ...ApplicationRolePreview
      files {
        edges {
          node {
            id
            __typename
            metadata {
              video
            }
            file
            ...Video
            ...ProjectMedia
          }
        }
      }
    }
  }
`)

interface Props {
  projectId: string | null | undefined
  onBack?: () => void
  onClose?: (open: boolean) => void
}
export const ProjectSideDrawerContent: React.FC<Props> = ({ projectId, onBack, onClose }) => {
  const handleClose = () => {
    onClose?.(false)
  }

  const [fetchProject] = useLazyQuery(GetSimpleProjectDocument, {
    variables: {
      projectId: decodeURIComponent(`${projectId}`),
    },
    context: {
      version: 2,
    },
  })

  const [projectData, setProjectData] = useState<GetSimpleProjectQuery | undefined>(undefined)

  useEffect(() => {
    async function fetchProjectData() {
      const res = await fetchProject()
      setProjectData(res.data)
    }
    if (Boolean(projectId)) {
      fetchProjectData()
    }
  }, [fetchProject, projectId])

  const project = projectData?.creativeWorkPortfolioProject
  const projectFiles = project?.files.edges.filter(edge => edge?.node.file) || []

  return (
    <div className="relative flex h-full w-full flex-col overflow-y-scroll">
      <div
        className={clsx(
          "relative left-0 top-0 flex h-[68px] w-full shrink-0 flex-row items-center",
          onBack && onClose && "justify-between",
          onBack && !onClose && "justify-start",
          !onBack && onClose && "justify-end"
        )}>
        {onBack ? (
          <>
            <TextLink
              className="hidden sm:flex"
              colorVariant="solid"
              text="Back"
              LeftIcon={ArrowLeftIcon}
              onClick={onBack}
            />
            <div className="fixed left-0 top-0 z-20 flex h-16 w-full items-center justify-center border-b border-b-bg-2 bg-bg-1 sm:hidden">
              <div className="absolute left-4">
                <ArrowLeftIcon onClick={onBack} className="size-[22px]" />
              </div>
              <Text variant="label">Project overview</Text>
            </div>
          </>
        ) : null}
        {onClose ? (
          <div className="fixed right-10 z-20">
            <IconButton Icon={XMarkIcon} variant="white" className="z-20" onClick={handleClose} />{" "}
          </div>
        ) : null}
      </div>
      <div className="flex flex-col gap-y-4 pb-10">
        <div className="flex flex-col gap-y-1">
          <Text variant="h5">{project?.title}</Text>
          <RolePreview
            withIcon={false}
            creativeWork={project}
            positionId={project?.id}
            textProps={{
              variant: "label",
            }}
          />
        </div>
        <div className={clsx("flex flex-col gap-y-6")}>
          {projectFiles.map((edge, index) => {
            return <ProjectMedia index={index} file={edge?.node} key={index} />
          })}
        </div>
      </div>
    </div>
  )
}
